import React, { useRef } from 'react';
import { number } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Sticky from 'react-stickynode';
import useStyles from 'hooks/useStyles';
import cx from 'classnames';
import history from 'history.js';

import {
  getLiveTopic,
  getLiveTopicInfo,
  getLiveFilterInfo,
} from 'reduxModules/live/selector';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';

import GetFreeTrialButton from 'components/GetFreeTrialButton';
import { CalendarIcon } from 'components/Icon';
import SnapaskHeader from 'components/SnapaskHeader';
import SnapaskFooter from 'components/SnapaskFooter';
import SnapaskLocaleSelection from 'components/SnapaskLocaleSelection';
import InstallAppForDesktopHandler from 'components/InstallAppForDesktopHandler';
import AlertBanner from 'components/AlertBanner';
import BreadcrumbSwitch from 'components/BreadcrumbSwitch';
import Tabs from 'components/Tabs';
import PaymentOptionList from 'components/PaymentOptionList';
import InstallAppForMobileHandler from 'components/InstallAppForMobileHandler';

import ContextStore from 'utils/createContext';
import useGetCurrentDevice from 'hooks/useGetCurrentDevice';

import { REGULAR_CLASS } from 'constants/contentTypes';
import {
  REGULAR_CLASS_PURCHASE_ENTER,
  REGULAR_CLASS_PURCHASE_CLICK,
  REGULAR_CLASS_SYLLABUS_LESSON_CLICK,
} from 'constants/gtmEvent';

import {
  ABOUT,
  SCHEDULE,
  INSTRUCTOR,
  MATERIALS,
  REVIEW,
  QUESTIONS,
  tabEventMapping,
} from '../constants/topicTabs';

import LiveLayout from '../components/LiveLayout';
import EpisodePlayer from '../components/EpisodePlayer';
import EpisodeList from '../components/EpisodeList';
import Materials from '../components/Materials';
import Overview from '../components/Overview';
import Instructor from '../components/Instructor';
import Review from '../components/Review';
import Questions from '../components/Questions';
import Schedule from '../components/Schedule';

import useGetPathList from '../hooks/useGetPathList';
import useRCPlaylist from '../hooks/useRCPlaylist';

import styles from './LiveTopic.scss';

const isTopicPagePath = path => {
  return path.indexOf('/topics/') > -1 && path.indexOf('/lesson/') === -1;
};

const RATING_ID = 'rating-section';

function LiveTopic({ id, lessonId }) {
  useStyles(styles);
  const dispatch = useDispatch();
  const { t } = useTranslation(['live']);
  const contentDomRef = useRef();
  const { location } = history;
  const { isMobile, isTablet } = useGetCurrentDevice();

  const {
    tabKeyList,
    freeLessonList,
    finishedLessons,
    scheduledLessons: { allLiveLessons },
    syllabuses,
  } = useSelector(getLiveTopic);
  const {
    pictureUrl,
    name,
    isEnrolled,
    active: isActive,
    isPreOrdered,
    enrollStartAt,
    isSemester,
    semesterExpiredDays,
    instructorIdList,
    instructorNameList,
  } = useSelector(getLiveTopicInfo);
  const paidLessonList = [...finishedLessons, ...allLiveLessons];
  const { selectedGradeLevel } = useSelector(getLiveFilterInfo);
  const {
    playerRef,
    currentLesson,
    onClickLesson,
    playCurrentLesson,
    playFirstFreeLesson,
    pauseCurrentLesson,
    playNextLesson,
    onEndedLesson,
    onVolumechange,
    updateLessonProgress,
    showAutoPlayController,
    nextLesson,
    isMuted,
    onProgressDragStart,
    onProgressDragEnd,
    onForwardStart,
    onRewindStart,
  } = useRCPlaylist({
    freeLessonList,
    // prevent auto-playing the disabled lesson
    paidLessonList: paidLessonList.filter(({ isDisabled }) => !isDisabled),
    initialLessonId: lessonId,
    liveTopicId: id,
    liveTopicName: name,
    defaultMuted: !isEnrolled,
    instructorIdList,
    instructorNameList,
  });

  const pathList = useGetPathList();
  const bundleViewMode = location?.hash === '#view';
  const showPaymentOption =
    !isEnrolled && !isPreOrdered && isActive && !bundleViewMode;
  const showEnrollPendingSection = isPreOrdered && !bundleViewMode;
  const tabComponentMapping = {
    [ABOUT]: {
      title: t('topic.overview.label'),
      component: <Overview ratingId={RATING_ID} />,
      order: 0,
    },
    [SCHEDULE]: {
      title: t('topic.overview.detail.upcoming'),
      component: <Schedule />,
      order: 1,
    },
    [MATERIALS]: {
      title: t(`topic.${MATERIALS}`),
      component: <Materials />,
      order: 2,
    },
    [REVIEW]: {
      title: t('topic.review'),
      component: <Review id={RATING_ID} />,
      order: 3,
    },
    [INSTRUCTOR]: {
      title: t('topic.overview.detail.instructor'),
      component: <Instructor />,
      order: 4,
    },
    [QUESTIONS]: {
      title: t('topic.overview.detail.faq'),
      component: <Questions />,
      order: 5,
    },
  };

  if (isEnrolled) {
    delete tabComponentMapping[SCHEDULE];
  }

  const onClickPaymentOption = () => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_PURCHASE_ENTER,
        data: {
          topicId: id,
          topicName: name,
          gradeLevelName: selectedGradeLevel.name,
        },
      }),
    );
  };

  const onSyllabusLessonClick = (lessonName, syllabusName) => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_SYLLABUS_LESSON_CLICK,
        data: {
          syllabusLessonName: lessonName,
          topicName: name,
          syllabusName,
        },
      }),
    );
  };

  const renderTabs = tabKeyList.reduce((tabs, value) => {
    const { title, component, order } = tabComponentMapping[value] || {};

    if (!component) return tabs;

    // eslint-disable-next-line no-param-reassign
    tabs[order] = (
      <Tabs.TabPane
        tabKey={value}
        key={value}
        tabTitle={title}
        className={styles.tabs}
      >
        {component}
      </Tabs.TabPane>
    );

    return tabs;
  }, Array(Object.keys(tabEventMapping).length));

  const onTabChange = tabName => {
    dispatch(
      sendGTMEvent({
        eventName: tabEventMapping[tabName],
      }),
    );
  };

  const onBreadcrumbClick = link => {
    if (isTopicPagePath(link)) {
      playFirstFreeLesson();
    }
  };

  const onClickMobilePaymentOptionButton = () => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_PURCHASE_CLICK,
        data: {
          topicId: id,
          topicName: name,
        },
      }),
    );
  };

  return (
    <LiveLayout>
      <SnapaskHeader />
      <AlertBanner />
      <SnapaskLocaleSelection />
      <InstallAppForMobileHandler />
      <div className={styles.root}>
        <ContextStore.Provider
          value={{
            isTopicEnrolled: isEnrolled,
            playerRef,
            viewMode: bundleViewMode,
            liveTopicId: id,
            liveTopicName: name,
            freeLessonList,
            finishedLessons,
            upcomingLessons: allLiveLessons,
            currentLesson: {
              ...currentLesson,
              instructorIdList,
              instructorNameList,
            },
            onClickLesson,
            playCurrentLesson,
            pauseCurrentLesson,
            onEndedLesson,
            onVolumechange,
            updateLessonProgress,
            playNextLesson,
            showAutoPlayController,
            nextLesson,
            isMuted,
            onProgressDragStart,
            onProgressDragEnd,
            onForwardStart,
            onRewindStart,
            isSemester,
            semesterExpiredDays,
            syllabuses,
          }}
        >
          <BreadcrumbSwitch
            pathList={pathList}
            className={styles.breadcrumb}
            onClick={onBreadcrumbClick}
          />
          <div className={styles.wrapper}>
            <EpisodePlayer
              className={styles.player}
              topicId={id}
              topicName={name}
              topicCoverImageUrl={pictureUrl}
              instructorIdList={instructorIdList}
              instructorNameList={instructorNameList}
            />
            <div className={styles.sidebar}>
              {showEnrollPendingSection && (
                <div className={styles.enrollPending}>
                  <CalendarIcon />
                  {t('live:topic.videoPlayerStatus.comingSoon.description', {
                    date: enrollStartAt,
                  })}
                </div>
              )}
              <EpisodeList
                showFreeTrial={!isEnrolled}
                className={styles.episodeList}
                onSyllabusLessonClick={onSyllabusLessonClick}
              />
              <div className={styles.installApp}>
                <InstallAppForDesktopHandler />
              </div>
              <Sticky
                top={88}
                enabled={!isMobile}
                bottomBoundary={
                  contentDomRef.current?.getBoundingClientRect().bottom
                }
              >
                <div
                  className={cx(styles.paymentOption, {
                    [styles.showPaymentOption]: showPaymentOption,
                  })}
                >
                  <PaymentOptionList
                    topicId={id}
                    topicName={name}
                    contentType={REGULAR_CLASS}
                    contentId={id}
                    onClickPaymentOption={onClickPaymentOption}
                    onClickCtaButton={onClickMobilePaymentOptionButton}
                  />
                </div>
              </Sticky>
              <GetFreeTrialButton
                isShow={(isMobile || isTablet) && !isEnrolled}
                reversed
                block
                type="regular_class"
                id={id}
                name={name}
                section="topic"
              />
            </div>
            <div className={styles.content} ref={contentDomRef}>
              <Tabs onChange={onTabChange} mode="anchor">
                {renderTabs}
              </Tabs>
            </div>
          </div>
        </ContextStore.Provider>
      </div>
      <SnapaskFooter />
    </LiveLayout>
  );
}

LiveTopic.propTypes = {
  id: number.isRequired,
  lessonId: number,
};

LiveTopic.defaultProps = {
  lessonId: undefined,
};

export default LiveTopic;
